import { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTenants, useCreateTenant, useToggleTenantStatus, useGenerateTenantApiKey } from './hooks/useTenants';
import { useSolutions } from '../metadata/hooks/useMetadata';
import { usePermissions, Permission } from '../../shared/hooks/usePermissions';
import Button from '../../shared/components/Button';
import { AddressAutocomplete } from '../../shared/components/AddressAutocomplete';
import { ApiKeyModal } from './components/ApiKeyModal';
import type { Tenant, CreateTenantPayload } from './types';

function formatDate(dateStr: string | null) {
  if (!dateStr) return '—';
  return new Date(dateStr).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' });
}

const inputCls = 'w-full bg-background text-foreground placeholder:text-muted-foreground border border-border rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-ring';
const labelCls = 'block text-sm font-medium text-foreground mb-1';
const optionalSpan = <span className="text-muted-foreground font-normal"> (optional)</span>;

// ─── Row actions dropdown ─────────────────────────────────────────────────────

interface RowActionsProps {
  tenant: Tenant;
  canDeactivate: boolean;
  canMintApiKey: boolean;
  onToggleStatus: () => void;
  onGenerateApiKey: () => void;
  isTogglingStatus: boolean;
  isGeneratingKey: boolean;
}

function RowActions({
  tenant, canDeactivate, canMintApiKey,
  onToggleStatus, onGenerateApiKey,
  isTogglingStatus, isGeneratingKey,
}: RowActionsProps) {
  const [open, setOpen] = useState(false);
  const [menuPos, setMenuPos] = useState({ top: 0, left: 0 });
  const triggerRef = useRef<HTMLButtonElement>(null);
  const menuRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    const handler = (e: MouseEvent) => {
      if (
        menuRef.current && !menuRef.current.contains(e.target as Node) &&
        triggerRef.current && !triggerRef.current.contains(e.target as Node)
      ) {
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [open]);

  const handleOpen = () => {
    if (triggerRef.current) {
      const rect = triggerRef.current.getBoundingClientRect();
      setMenuPos({ top: rect.bottom + 4, left: rect.right - 192 });
    }
    setOpen((o) => !o);
  };

  if (!canDeactivate && !canMintApiKey) return null;

  const itemCls = 'w-full text-left px-4 py-2 text-sm hover:bg-muted transition-colors disabled:opacity-50 disabled:cursor-not-allowed';

  return (
    <div onClick={(e) => e.stopPropagation()}>
      <button
        ref={triggerRef}
        onClick={handleOpen}
        className="p-1.5 rounded-md hover:bg-muted text-foreground transition-colors"
        aria-label="Row actions"
      >
        <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
          <circle cx="12" cy="5" r="1.5" /><circle cx="12" cy="12" r="1.5" /><circle cx="12" cy="19" r="1.5" />
        </svg>
      </button>

      {open && (
        <div
          ref={menuRef}
          style={{ position: 'fixed', top: menuPos.top, left: menuPos.left, zIndex: 9999 }}
          className="w-48 bg-card border border-border rounded-[10px] shadow-lg overflow-hidden"
        >
          {canMintApiKey && (
            <button
              className={itemCls}
              disabled={isGeneratingKey || tenant.invitePending}
              title={tenant.invitePending ? 'Tenant must complete onboarding before an API key can be issued' : undefined}
              onClick={() => { onGenerateApiKey(); setOpen(false); }}
            >
              {tenant.hasApiKey ? 'Rotate API key' : 'Generate API key'}
            </button>
          )}
          {canDeactivate && (
            <button
              className={`${itemCls} ${tenant.active ? 'text-red-600' : 'text-green-700'}`}
              disabled={isTogglingStatus}
              onClick={() => { onToggleStatus(); setOpen(false); }}
            >
              {tenant.active ? 'Deactivate' : 'Activate'}
            </button>
          )}
        </div>
      )}
    </div>
  );
}

// ─── Create modal ─────────────────────────────────────────────────────────────

function CreateTenantModal({ onClose }: { onClose: () => void }) {
  const { data: solutions = [] } = useSolutions();
  const createMutation = useCreateTenant();
  const [error, setError] = useState('');
  const [form, setForm] = useState({
    name: '',
    solutionId: '',
    companyAddress: '',
    clarityProjectId: '',
    adminFullName: '',
    adminEmail: '',
    phoneNumber: '',
  });

  const set = (key: keyof typeof form, val: string) =>
    setForm((f) => ({ ...f, [key]: val }));

  const validate = () => {
    setError('');
    if (form.name.trim().length < 2) { setError('Tenant name must be at least 2 characters'); return false; }
    if (form.companyAddress.trim() && form.companyAddress.trim().length < 5) { setError('Company address must be at least 5 characters'); return false; }
    if (form.adminFullName.trim().length < 2) { setError('Admin full name must be at least 2 characters'); return false; }
    if (!form.adminEmail.trim()) { setError('Admin email is required'); return false; }
    return true;
  };

  const handleSubmit = async (e: React.SyntheticEvent) => {
    e.preventDefault();
    if (!validate()) return;
    // The server forces type=BRAND_WEBSITE and derives the slug from the name.
    const payload: CreateTenantPayload = {
      name: form.name.trim(),
      adminEmail: form.adminEmail.trim(),
      adminFullName: form.adminFullName.trim(),
      ...(form.phoneNumber.trim() ? { phoneNumber: form.phoneNumber.trim() } : {}),
      ...(form.companyAddress.trim() ? { companyAddress: form.companyAddress.trim() } : {}),
      ...(form.clarityProjectId.trim() ? { clarityProjectId: form.clarityProjectId.trim() } : {}),
      ...(form.solutionId ? { solutionId: form.solutionId } : {}),
    };
    try {
      await createMutation.mutateAsync(payload);
      onClose();
    } catch (err: unknown) {
      setError((err as Error)?.message || 'Something went wrong');
    }
  };

  const isLoading = createMutation.isPending;

  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
      <div className="bg-card rounded-lg w-full max-w-2xl max-h-[90vh] flex flex-col">
        <div className="p-5 border-b flex items-center justify-between shrink-0">
          <h2 className="text-lg font-bold text-foreground">Create Tenant</h2>
          <button type="button" onClick={onClose} className="text-muted-foreground hover:text-foreground">
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M18 6L6 18M6 6l12 12" strokeLinecap="round" />
            </svg>
          </button>
        </div>

        <form onSubmit={handleSubmit} className="flex-1 p-5 space-y-4 overflow-y-auto">
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className={labelCls}>Tenant Name</label>
              <input required className={inputCls} value={form.name} placeholder="e.g. The Collective Synergy"
                onChange={(e) => set('name', e.target.value)} />
            </div>
            <div>
              <label className={labelCls}>Solution {optionalSpan}</label>
              <select className={inputCls} value={form.solutionId} onChange={(e) => set('solutionId', e.target.value)}>
                <option value="">Collective (default)</option>
                {solutions.filter((s) => s.isActive).map((s) => (
                  <option key={s.slug} value={s.slug}>{s.name}</option>
                ))}
              </select>
            </div>
          </div>
          <div>
            <label className={labelCls}>Company Address {optionalSpan}</label>
            <AddressAutocomplete
              value={form.companyAddress}
              onChange={(val) => set('companyAddress', val)}
              onPlaceSelect={(data) => set('companyAddress', data.formattedAddress)}
              placeholder="e.g. 123 Main St, London, UK"
              disabled={isLoading}
            />
          </div>
          <div>
            <label className={labelCls}>Clarity Project ID {optionalSpan}</label>
            <input className={inputCls} value={form.clarityProjectId} placeholder="e.g. x16mnvcxxt"
              onChange={(e) => set('clarityProjectId', e.target.value)} />
            <p className="text-xs text-muted-foreground mt-1">Microsoft Clarity project id for session recordings on this tenant's website.</p>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className={labelCls}>Admin Full Name</label>
              <input required placeholder="e.g. Jane Doe" className={inputCls}
                value={form.adminFullName} onChange={(e) => set('adminFullName', e.target.value)} />
            </div>
            <div>
              <label className={labelCls}>Admin Phone {optionalSpan}</label>
              <input type="tel" placeholder="e.g. +44 7700 900000" className={inputCls}
                value={form.phoneNumber} onChange={(e) => set('phoneNumber', e.target.value)} />
            </div>
          </div>
          <div>
            <label className={labelCls}>Admin Email</label>
            <input required type="email" placeholder="e.g. jane@brand.com" className={inputCls}
              value={form.adminEmail} onChange={(e) => set('adminEmail', e.target.value)} />
          </div>
          <div className="rounded-md bg-blue-50 border border-blue-200 p-3 text-sm text-blue-700">
            A brand-website tenant will be created with a URL slug derived from its name, and an
            invite email will be sent to this address once you click <strong>Create Tenant</strong>.
            The link expires in 48 hours.
          </div>

          {error && <p className="text-red-500 text-sm">{error}</p>}
        </form>

        <div className="p-5 border-t flex items-center justify-between shrink-0">
          <button type="button" onClick={onClose}
            className="px-4 py-2 text-sm font-medium text-foreground border border-border rounded-md hover:bg-muted">
            Cancel
          </button>
          <Button loading={isLoading} onClick={handleSubmit}>
            Create Tenant
          </Button>
        </div>
      </div>
    </div>
  );
}

// ─── Main page ────────────────────────────────────────────────────────────────

export default function Tenants() {
  const navigate = useNavigate();
  const { data: tenants = [], isLoading, error } = useTenants();
  const { data: solutions = [] } = useSolutions();
  const toggleStatus = useToggleTenantStatus();
  const generateKey = useGenerateTenantApiKey();
  const { hasPermission } = usePermissions();

  const [showCreate, setShowCreate] = useState(false);
  const [newKey, setNewKey] = useState<string | null>(null);

  const canCreate = hasPermission(Permission.TENANT_CREATE);
  const canDeactivate = hasPermission(Permission.TENANT_DEACTIVATE);
  const canMintApiKey = hasPermission(Permission.API_KEY_CREATE);

  const solutionName = (slug: string) => solutions.find((s) => s.slug === slug)?.name ?? slug;

  const handleGenerateKey = async (id: string) => {
    try {
      const result = await generateKey.mutateAsync(id);
      setNewKey(result.apiKey);
    } catch {
      // Error toast already raised by the mutation's onError.
    }
  };

  return (
    <div className="space-y-6 py-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-foreground">Tenants</h1>
          <p className="text-sm text-foreground mt-1">
            {tenants.length} brand-website tenant{tenants.length !== 1 ? 's' : ''}
          </p>
        </div>
        {canCreate && (
          <Button onClick={() => setShowCreate(true)}>Add Tenant</Button>
        )}
      </div>

      {isLoading && <div className="text-center py-12 text-foreground">Loading tenants…</div>}
      {error && <div className="text-center py-12 text-red-500">Failed to load tenants.</div>}

      {!isLoading && !error && (
        <div className="bg-card rounded-lg border border-border overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-border bg-muted">
                  <th className="text-left px-4 py-3 font-semibold text-foreground">Name</th>
                  <th className="text-left px-4 py-3 font-semibold text-foreground">Slug</th>
                  <th className="text-left px-4 py-3 font-semibold text-foreground">Solution</th>
                  <th className="text-left px-4 py-3 font-semibold text-foreground">API Key</th>
                  <th className="text-left px-4 py-3 font-semibold text-foreground">Status</th>
                  <th className="text-left px-4 py-3 font-semibold text-foreground">Created</th>
                  <th className="px-4 py-3 w-10"></th>
                </tr>
              </thead>
              <tbody>
                {tenants.length === 0 ? (
                  <tr>
                    <td colSpan={7} className="text-center py-12 text-foreground">No tenants yet.</td>
                  </tr>
                ) : (
                  tenants.map((tenant) => (
                    <tr key={tenant.id}
                      onClick={() => navigate(`/tenants/${tenant.id}`)}
                      className="border-b border-border hover:bg-muted cursor-pointer">
                      <td className="px-4 py-3 font-medium text-foreground">
                        <div className="flex items-center gap-2">
                          {tenant.name}
                          {tenant.invitePending && (
                            <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-amber-100 text-amber-800">
                              Invite pending
                            </span>
                          )}
                        </div>
                      </td>
                      <td className="px-4 py-3 text-foreground font-mono text-xs">{tenant.slug}</td>
                      <td className="px-4 py-3 text-foreground">{solutionName(tenant.solutionId)}</td>
                      <td className="px-4 py-3">
                        <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${
                          tenant.hasApiKey ? 'bg-green-100 text-green-800' : 'bg-muted text-muted-foreground'
                        }`}>
                          {tenant.hasApiKey ? 'Issued' : 'None'}
                        </span>
                      </td>
                      <td className="px-4 py-3">
                        <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${
                          tenant.active ? 'bg-green-100 text-green-800' : 'bg-muted text-muted-foreground'
                        }`}>
                          {tenant.active ? 'Active' : 'Inactive'}
                        </span>
                      </td>
                      <td className="px-4 py-3 text-foreground">{formatDate(tenant.createdAt)}</td>
                      <td className="px-4 py-3">
                        <RowActions
                          tenant={tenant}
                          canDeactivate={canDeactivate}
                          canMintApiKey={canMintApiKey}
                          onToggleStatus={() => toggleStatus.mutate({ id: tenant.id, active: tenant.active })}
                          onGenerateApiKey={() => handleGenerateKey(tenant.id)}
                          isTogglingStatus={toggleStatus.isPending}
                          isGeneratingKey={generateKey.isPending}
                        />
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {showCreate && <CreateTenantModal onClose={() => setShowCreate(false)} />}
      {newKey && <ApiKeyModal apiKey={newKey} onClose={() => setNewKey(null)} />}
    </div>
  );
}
